import { useState } from "preact/hooks";
import { errorMessage, login, register } from "../../api/client";
import type { FullAuthResponse } from "../../api/types";
import { validateDisplayName, validatePassword, validateUsername } from "../../app/validation";
import { Button } from "./Button";
import { Field, TextInput } from "./Field";

export type AccountMode = "login" | "register";

export interface AccountFormProps {
  mode: AccountMode;
  /** Receives the server response; the caller applies it (`applyAuth` / `completeSignIn`). */
  onDone: (res: FullAuthResponse) => void;
  /** Register only: prefilled optional display name (the guest's current name). */
  initialDisplayName?: string;
}

/** Login or register form with client-side validation and the server's error message inline. */
export function AccountForm({ mode, onDone, initialDisplayName = "" }: AccountFormProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  const registering = mode === "register";
  // Login only needs non-empty credentials; the server decides whether they are right.
  const usernameError = registering ? validateUsername(username) : username ? null : "Enter your username.";
  const passwordError = registering ? validatePassword(password) : password ? null : "Enter your password.";
  const nameError = registering && displayName.trim() ? validateDisplayName(displayName) : null;
  const valid = !usernameError && !passwordError && !nameError;

  const submit = async () => {
    setSubmitted(true);
    setServerError("");
    if (!valid || busy) return;
    setBusy(true);
    try {
      const res = registering
        ? await register({ username, password, displayName: displayName.trim() || undefined })
        : await login(username, password);
      onDone(res);
    } catch (e) {
      setServerError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      class="account-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Field label="Username" hint={registering ? "3-16 letters, digits or underscores" : undefined} error={submitted ? usernameError : null}>
        {(id) => (
          <TextInput
            id={id}
            name="username"
            value={username}
            onValue={setUsername}
            maxLength={16}
            autoComplete="username"
            invalid={submitted && usernameError !== null}
            disabled={busy}
          />
        )}
      </Field>
      <Field label="Password" hint={registering ? "At least 8 characters" : undefined} error={submitted ? passwordError : null}>
        {(id) => (
          <TextInput
            id={id}
            name="password"
            type="password"
            value={password}
            onValue={setPassword}
            maxLength={128}
            autoComplete={registering ? "new-password" : "current-password"}
            invalid={submitted && passwordError !== null}
            disabled={busy}
          />
        )}
      </Field>
      {registering && (
        <Field label="Display name" hint="Optional, shown to other players" error={submitted ? nameError : null}>
          {(id) => (
            <TextInput
              id={id}
              name="displayName"
              value={displayName}
              onValue={setDisplayName}
              maxLength={20}
              autoComplete="nickname"
              invalid={submitted && nameError !== null}
              disabled={busy}
            />
          )}
        </Field>
      )}
      <div class="account-form__status" role="alert">
        {serverError && <div class="notice notice--error">{serverError}</div>}
      </div>
      <Button type="submit" variant="primary" busy={busy} full>
        {registering ? "Create account" : "Log in"}
      </Button>
    </form>
  );
}
